import React, { createContext, useContext, useState, ReactNode } from 'react';

interface Client {
  id: number;
  auditClientCode: string;
  companySecretarialCode: string;
  companyName: string;
  partner: string;
  group: string;
  brNumber: string;
  stockTake: string;
  latestAnnualAuditYearEndDate: string;
  taxReturnsOutstanding: string;
  taxReturnsOutstandingYear: string;
  annualReturnFiled: string;
  agent: string;
  registeredOffice: string;
  clientContact: string;
  active: boolean;
  services: string[];
}

interface Engagement {
  id: number;
  engagementCode: string;
  auditClientCode: string;
  companySecretarialCode: string;
  companyName: string;
  partner: string;
  auditMgr: string;
  auditInCharge: string;
  accountInCharge: string;
  yearEndDate: string;
  expectedDeadline: string;
  bankConf: string | string[];
  taxReturns: string;
  invoice: string;
  receipt: string;
  status: string;
  taxReturnsFiled: string;
  opinion: string;
  typeOfEngagement: string;
  document: string | string[];
  services: string[];
}

interface AppContextType {
  clients: Client[];
  engagements: Engagement[];
  addClient: (client: Client) => void;
  addEngagement: (engagement: Engagement) => void;
  updateClient: (clientId: number, field: keyof Client, value: any) => void;
  updateEngagement: (engagementId: number, field: keyof Engagement, value: any) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [clients, setClients] = useState<Client[]>([
    {
      id: 1,
      auditClientCode: 'A001',
      companySecretarialCode: 'CA001',
      companyName: 'ABC Company Limited',
      partner: 'Jacky',
      group: 'Group A',
      brNumber: '12345678',
      stockTake: 'Yes',
      latestAnnualAuditYearEndDate: '2023-12-31',
      taxReturnsOutstanding: 'No',
      taxReturnsOutstandingYear: '',
      annualReturnFiled: 'Yes',
      agent: 'Agent A',
      registeredOffice: '123 Main St',
      clientContact: 'contact@abc.com',
      active: true,
      services: ['audit', 'account']
    },
    {
      id: 2,
      auditClientCode: 'X001',
      companySecretarialCode: 'CX001',
      companyName: 'XYZ Corporation',
      partner: 'Jacky',
      group: 'Group B',
      brNumber: '87654321',
      stockTake: 'No',
      latestAnnualAuditYearEndDate: '2023-09-30',
      taxReturnsOutstanding: 'Yes',
      taxReturnsOutstandingYear: '2023',
      annualReturnFiled: 'No',
      agent: 'Agent B',
      registeredOffice: '456 Oak Ave',
      clientContact: 'info@xyz.com',
      active: true,
      services: ['audit', 'company secretary']
    },
    {
      id: 3,
      auditClientCode: 'D003',
      companySecretarialCode: 'CD001',
      companyName: 'DEF Industries',
      partner: 'Jacky',
      group: 'Group C',
      brNumber: '11223344',
      stockTake: 'Yes',
      latestAnnualAuditYearEndDate: '2023-06-30',
      taxReturnsOutstanding: 'No',
      taxReturnsOutstandingYear: '',
      annualReturnFiled: 'Yes',
      agent: 'Agent C',
      registeredOffice: '789 Pine Rd',
      clientContact: 'hello@def.com',
      active: false,
      services: ['account only']
    },
    {
      id: 4,
      auditClientCode: 'E004',
      companySecretarialCode: 'CE001',
      companyName: 'EFG Enterprises',
      partner: 'Jacky',
      group: 'Group D',
      brNumber: '22334455',
      stockTake: 'No',
      latestAnnualAuditYearEndDate: '2023-11-30',
      taxReturnsOutstanding: 'No',
      taxReturnsOutstandingYear: '',
      annualReturnFiled: 'Yes',
      agent: 'Agent D',
      registeredOffice: '321 Elm St',
      clientContact: 'contact@efg.com',
      active: true,
      services: ['audit']
    },
    {
      id: 5,
      auditClientCode: 'F005',
      companySecretarialCode: 'CF001',
      companyName: 'FGH Manufacturing',
      partner: 'Henry',
      group: 'Group E',
      brNumber: '33445566',
      stockTake: 'Yes',
      latestAnnualAuditYearEndDate: '2023-10-31',
      taxReturnsOutstanding: 'Yes',
      taxReturnsOutstandingYear: '2023',
      annualReturnFiled: 'No',
      agent: 'Agent E',
      registeredOffice: '654 Maple Ave',
      clientContact: 'info@fgh.com',
      active: true,
      services: ['company secretary', 'account']
    },
    {
      id: 6,
      auditClientCode: 'G006',
      companySecretarialCode: 'CG001',
      companyName: 'GHI Services',
      partner: 'Henry',
      group: 'Group F',
      brNumber: '44556677',
      stockTake: 'No',
      latestAnnualAuditYearEndDate: '2023-08-31',
      taxReturnsOutstanding: 'No',
      taxReturnsOutstandingYear: '',
      annualReturnFiled: 'Yes',
      agent: 'Agent F',
      registeredOffice: '987 Cedar Blvd',
      clientContact: 'contact@ghi.com',
      active: true,
      services: ['audit', 'account', 'company secretary']
    },
    {
      id: 7,
      auditClientCode: 'H007',
      companySecretarialCode: 'CH001',
      companyName: 'HIJ Technologies',
      partner: 'Henry',
      group: 'Group G',
      brNumber: '55667788',
      stockTake: 'Yes',
      latestAnnualAuditYearEndDate: '2023-12-15',
      taxReturnsOutstanding: 'No',
      taxReturnsOutstandingYear: '',
      annualReturnFiled: 'Yes',
      agent: 'Agent G',
      registeredOffice: '147 Birch Ln',
      clientContact: 'info@hij.com',
      active: true,
      services: ['audit']
    },
    {
      id: 8,
      auditClientCode: 'I008',
      companySecretarialCode: 'CI001',
      companyName: 'IJK Solutions',
      partner: 'Kenneth',
      group: 'Group H',
      brNumber: '66778899',
      stockTake: 'No',
      latestAnnualAuditYearEndDate: '2023-07-31',
      taxReturnsOutstanding: 'Yes',
      taxReturnsOutstandingYear: '2023',
      annualReturnFiled: 'No',
      agent: 'Agent H',
      registeredOffice: '258 Oak Dr',
      clientContact: 'contact@ijk.com',
      active: false,
      services: ['account only']
    },
    {
      id: 9,
      auditClientCode: 'J009',
      companySecretarialCode: 'CJ001',
      companyName: 'JKL Innovations',
      partner: 'Kenneth',
      group: 'Group I',
      brNumber: '77889900',
      stockTake: 'Yes',
      latestAnnualAuditYearEndDate: '2023-11-15',
      taxReturnsOutstanding: 'No',
      taxReturnsOutstandingYear: '',
      annualReturnFiled: 'Yes',
      agent: 'Agent I',
      registeredOffice: '369 Pine St',
      clientContact: 'contact@jkl.com',
      active: true,
      services: ['company secretary']
    },
    {
      id: 10,
      auditClientCode: 'K010',
      companySecretarialCode: 'CK001',
      companyName: 'KLM Partners',
      partner: 'Kenneth',
      group: 'Group J',
      brNumber: '88990011',
      stockTake: 'No',
      latestAnnualAuditYearEndDate: '2023-09-15',
      taxReturnsOutstanding: 'No',
      taxReturnsOutstandingYear: '',
      annualReturnFiled: 'Yes',
      agent: 'Agent J',
      registeredOffice: '741 Elm Ave',
      clientContact: 'info@klm.com',
      active: true,
      services: ['audit', 'account']
    },
    {
      id: 11,
      auditClientCode: 'L011',
      companySecretarialCode: 'CL001',
      companyName: 'LMN Holdings',
      partner: 'Kenneth',
      group: 'Group K',
      brNumber: '99001122',
      stockTake: 'Yes',
      latestAnnualAuditYearEndDate: '2023-10-15',
      taxReturnsOutstanding: 'Yes',
      taxReturnsOutstandingYear: '2023',
      annualReturnFiled: 'No',
      agent: 'Agent K',
      registeredOffice: '852 Maple Blvd',
      clientContact: 'contact@lmn.com',
      active: true,
      services: ['account', 'company secretary']
    },
    {
      id: 12,
      auditClientCode: 'M012',
      companySecretarialCode: 'CM001',
      companyName: 'MNO Ventures',
      partner: 'Kenneth',
      group: 'Group L',
      brNumber: '00112233',
      stockTake: 'No',
      latestAnnualAuditYearEndDate: '2023-12-01',
      taxReturnsOutstanding: 'No',
      taxReturnsOutstandingYear: '',
      annualReturnFiled: 'Yes',
      agent: 'Agent L',
      registeredOffice: '963 Cedar Rd',
      clientContact: 'info@mno.com',
      active: true,
      services: ['audit', 'company secretary']
    }
  ]);

  const [engagements, setEngagements] = useState<Engagement[]>([
    {
      id: 1,
      engagementCode: 'AUD20260001',
      auditClientCode: 'A001',
      companySecretarialCode: 'CA001',
      companyName: 'ABC Company Limited',
      partner: 'Jacky',
      auditMgr: 'Nicole Lai',
      auditInCharge: 'Harry Yip',
      accountInCharge: 'Harry Yip',
      yearEndDate: '2025-12-31',
      expectedDeadline: '2026-06-30',
      bankConf: 'Pending',
      taxReturns: 'Completed',
      invoice: 'Completed',
      receipt: 'Received',
      status: 'Audit-in-charge start',
      taxReturnsFiled: 'Yes',
      opinion: 'Disclaimer',
      typeOfEngagement: 'Audit',
      document: 'Yes',
      services: ['audit', 'account']
    },
    {
      id: 2,
      engagementCode: 'GCS20260002',
      auditClientCode: 'X001',
      companySecretarialCode: 'CX001',
      companyName: 'XYZ Corporation',
      partner: 'Jacky',
      auditMgr: 'Nicole Lai',
      auditInCharge: 'Harry Yip',
      accountInCharge: 'Harry Yip',
      yearEndDate: '2025-12-31',
      expectedDeadline: '2026-06-30',
      bankConf: 'Completed',
      taxReturns: 'Pending',
      invoice: 'Pending',
      receipt: 'Not Received',
      status: 'Manager review',
      taxReturnsFiled: 'No',
      opinion: 'Disclaimer',
      typeOfEngagement: 'Review',
      document: 'No',
      services: ['Annual Company Secretary Services', 'Registered Office Services']
    },
    {
      id: 3,
      engagementCode: 'AUD20260003',
      auditClientCode: 'D001',
      companySecretarialCode: 'CD001',
      companyName: 'DEF Industries',
      partner: 'Jacky',
      auditMgr: 'Nicole Lai',
      auditInCharge: 'Harry Yip',
      accountInCharge: 'Harry Yip',
      yearEndDate: '2025-12-31',
      expectedDeadline: '2026-12-31',
      bankConf: 'Completed',
      taxReturns: 'Completed',
      invoice: 'Paid',
      receipt: 'Received',
      status: 'Partner review',
      taxReturnsFiled: 'Yes',
      opinion: 'Qualified',
      typeOfEngagement: 'Audit',
      document: 'Yes',
      services: ['account only']
    }
  ]);

  const addClient = (client: Client) => {
    setClients(prev => [...prev, client]);

    // Create corresponding engagements based on services
    const newEngagements: Engagement[] = [];

    if (client.services.includes('audit') || client.services.includes('account + audit')) {
      const auditEngagement: Engagement = {
        id: engagements.length > 0 ? Math.max(...engagements.map(e => e.id)) + 1 : 1,
        engagementCode: `AUD2026${String(client.id).padStart(4, '0')}`,
        auditClientCode: client.auditClientCode,
        companySecretarialCode: client.companySecretarialCode,
        companyName: client.companyName,
        partner: client.partner,
        auditMgr: 'TBD',
        auditInCharge: 'TBD',
        accountInCharge: client.services.includes('account + audit') ? 'TBD' : '',
        yearEndDate: 'TBD',
        expectedDeadline: 'TBD',
        bankConf: 'TBD',
        taxReturns: 'TBD',
        invoice: 'TBD',
        receipt: 'TBD',
        status: 'Audit-in-charge start',
        taxReturnsFiled: 'TBD',
        opinion: 'TBD',
        typeOfEngagement: 'TBD',
        document: 'TBD',
        services: client.services
      };
      newEngagements.push(auditEngagement);
    }

    if (client.services.includes('account') || client.services.includes('account only') || client.services.includes('account + audit')) {
      const accountingEngagement: Engagement = {
        id: engagements.length > 0 ? Math.max(...engagements.map(e => e.id)) + 1 : 1,
        engagementCode: `ACC2026${String(client.id).padStart(4, '0')}`,
        auditClientCode: client.auditClientCode,
        companySecretarialCode: client.companySecretarialCode,
        companyName: client.companyName,
        partner: client.partner,
        auditMgr: 'TBD',
        auditInCharge: 'TBD',
        accountInCharge: 'TBD',
        yearEndDate: 'TBD',
        expectedDeadline: 'TBD',
        bankConf: 'TBD',
        taxReturns: 'TBD',
        invoice: 'TBD',
        receipt: 'TBD',
        status: 'document received',
        taxReturnsFiled: 'TBD',
        opinion: 'TBD',
        typeOfEngagement: 'TBD',
        document: 'TBD',
        services: client.services
      };
      newEngagements.push(accountingEngagement);
    }

    if (client.services.includes('company secretary')) {
      const secretaryEngagement: Engagement = {
        id: engagements.length > 0 ? Math.max(...engagements.map(e => e.id)) + 1 : 1,
        engagementCode: `GCS2026${String(client.id).padStart(4, '0')}`,
        auditClientCode: client.auditClientCode,
        companySecretarialCode: client.companySecretarialCode,
        companyName: client.companyName,
        partner: client.partner,
        auditMgr: 'TBD',
        auditInCharge: 'TBD',
        accountInCharge: 'TBD',
        yearEndDate: 'TBD',
        expectedDeadline: 'TBD',
        bankConf: 'TBD',
        taxReturns: 'TBD',
        invoice: 'TBD',
        receipt: 'TBD',
        status: 'document preparing',
        taxReturnsFiled: 'TBD',
        opinion: 'TBD',
        typeOfEngagement: 'TBD',
        document: 'TBD',
        services: client.services
      };
      newEngagements.push(secretaryEngagement);
    }

    if (newEngagements.length > 0) {
      setEngagements(prev => [...prev, ...newEngagements]);
    }
  };

  const addEngagement = (engagement: Engagement) => {
    setEngagements(prev => [...prev, engagement]);
  };

  const updateClient = (clientId: number, field: keyof Client, value: any) => {
    setClients(prev => 
      prev.map(client => 
        client.id === clientId ? { ...client, [field]: value } : client
      )
    );
  };

  const updateEngagement = (engagementId: number, field: keyof Engagement, value: any) => {
    setEngagements(prev => 
      prev.map(engagement => 
        engagement.id === engagementId ? { ...engagement, [field]: value } : engagement
      )
    );
  };

  return (
    <AppContext.Provider value={{
      clients,
      engagements,
      addClient,
      addEngagement,
      updateClient,
      updateEngagement
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};